/**
 * arancelLookup.ts — Local lookup against arancel_1881 for instant
 * resolution of % Arancel / % IVA when the user types an HS Code in the
 * simulator. Mirrors the cascade strategy used by the costing RPC:
 *   1. exact 10-digit match
 *   2. 6-digit prefix LIKE search
 *   3. fallback to gravamen=0, iva=19
 *
 * Results are memoized per sanitized HS code in an in-memory cache to
 * avoid round-trips on retypes.
 */

import { supabase } from "@/integrations/supabase/client";

export interface ArancelLookupResult {
  gravamen_porcentaje: number;
  iva_porcentaje: number;
  matched: boolean;
  matched_codigo?: string;
}

const cache = new Map<string, ArancelLookupResult>();
const inflight = new Map<string, Promise<ArancelLookupResult>>();

const FALLBACK: ArancelLookupResult = {
  gravamen_porcentaje: 0,
  iva_porcentaje: 19,
  matched: false,
};

export function sanitize(raw: string): string {
  return (raw ?? "").replace(/\D/g, "").trim();
}

export async function lookupArancel(rawCode: string): Promise<ArancelLookupResult> {
  const clean = sanitize(rawCode);
  if (clean.length < 6) return FALLBACK;

  if (cache.has(clean)) return cache.get(clean)!;
  if (inflight.has(clean)) return inflight.get(clean)!;

  const promise = (async (): Promise<ArancelLookupResult> => {
    const padded = clean.length < 10 ? clean.padEnd(10, "0") : clean.slice(0, 10);

    // Exact match
    const { data: exact } = await supabase
      .from("arancel_1881")
      .select("codigo_partida,gravamen_porcentaje,iva_porcentaje")
      .eq("codigo_partida", padded)
      .maybeSingle();

    let row = exact;
    if (!row) {
      const prefix = clean.slice(0, 6);
      const { data: prefixed } = await supabase
        .from("arancel_1881")
        .select("codigo_partida,gravamen_porcentaje,iva_porcentaje")
        .like("codigo_partida", `${prefix}%`)
        .limit(1)
        .maybeSingle();
      row = prefixed ?? null;
    }

    const result: ArancelLookupResult = row
      ? {
          gravamen_porcentaje: Number(row.gravamen_porcentaje) || 0,
          iva_porcentaje: Number(row.iva_porcentaje) || 0,
          matched: true,
          matched_codigo: row.codigo_partida,
        }
      : FALLBACK;

    cache.set(clean, result);
    inflight.delete(clean);
    return result;
  })();

  inflight.set(clean, promise);
  return promise;
}
